import {
    useEffect,
    useRef,
    useState
} from "react";

import { useNavigate } from "react-router-dom";

import {
    ArrowLeft,
    Search,
    Plus,
    ChevronLeft,
    ChevronRight,
    Check,
    X,
    Pencil,
    Camera,
    Trash2,
    Image as ImageIcon
} from "lucide-react";

import api from "../services/api";


function Products() {

    const navigate = useNavigate();

    const [products, setProducts] =
        useState([]);

    const [search, setSearch] =
        useState("");

    const [page, setPage] =
        useState(1);

    const [totalPages, setTotalPages] =
        useState(1);

    const [totalProducts, setTotalProducts] =
        useState(0);

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState("");

    const [permissions, setPermissions] =
        useState([]);

    const [userRole, setUserRole] =
        useState("");

    const [editingCell, setEditingCell] =
        useState(null);

    const [editValue, setEditValue] =
        useState("");

    const [savingCell, setSavingCell] =
        useState(false);

    const [showCreateProduct, setShowCreateProduct] =
        useState(false);

    const [newProductName, setNewProductName] =
        useState("");

    const [newProductPrice, setNewProductPrice] =
        useState("");

    const [creatingProduct, setCreatingProduct] =
        useState(false);

    const [uploadingProductId, setUploadingProductId] =
        useState(null);

    const [changingStockId, setChangingStockId] =
        useState(null);

    const [deletingProductId, setDeletingProductId] =
        useState(null);

    const [pendingDeleteProduct, setPendingDeleteProduct] =
        useState(null);

    const [imagePreview, setImagePreview] =
        useState(null);

    const imageInputRef =
        useRef(null);

    const selectedImageProductRef =
        useRef(null);


    // ========================================
    // API SERVER ROOT
    // ========================================

    const apiBaseURL =
        api.defaults.baseURL ||
        "http://localhost:5000/api";

    const serverRoot =
        apiBaseURL.replace(
            /\/api\/?$/,
            ""
        );


    // ========================================
    // ACCESS
    // ========================================

    const loadAccess = async () => {

        try {

            const response =
                await api.get(
                    "/auth/me"
                );

            setUserRole(
                response.data.user?.role || ""
            );

            setPermissions(
                response.data.permissions || []
            );

        } catch (error) {

            console.error(
                "Unable to load permissions",
                error
            );
        }
    };


    const hasPermission = (
        permissionKey
    ) => {

        if (userRole === "OWNER") {
            return true;
        }

        return permissions.some(
            permission =>
                permission.permission_key ===
                permissionKey
        );
    };


    // ========================================
    // COLUMN VISIBILITY
    // ========================================

    const canViewCTN =
        hasPermission(
            "VIEW_CTN"
        );

    const canViewQuantity =
        hasPermission(
            "VIEW_QUANTITY"
        );

    const canViewUnit =
        hasPermission(
            "VIEW_UNIT"
        );

    const canViewSellingPrice =
        hasPermission(
            "VIEW_SELLING_PRICE"
        );

    const canViewLocation =
        hasPermission(
            "VIEW_LOCATION"
        );

    const canViewCostPrice =
        hasPermission(
            "VIEW_COST_PRICE"
        );

    const canViewItemCode =
        hasPermission(
            "VIEW_ITEM_CODE"
        );

    const canManageStock =
        hasPermission(
            "MANAGE_STOCK_STATUS"
        );


    // ========================================
    // DYNAMIC TABLE WIDTH
    // ========================================

    const staffTableMinWidth =
        210 +
        (canViewSellingPrice ? 130 : 0) +
        (canViewUnit ? 100 : 0) +
        (canViewCTN ? 90 : 0) +
        (canViewLocation ? 130 : 0) +
        (canViewQuantity ? 110 : 0) +
        (canViewCostPrice ? 130 : 0) +
        (canViewItemCode ? 130 : 0) +
        120;

    const tableMinWidth =
        userRole === "OWNER"
            ? "1150px"
            : `${staffTableMinWidth}px`;


    // ========================================
    // LOAD PRODUCTS
    // ========================================

    const loadProducts = async (
        selectedPage = 1,
        searchText = ""
    ) => {

        try {

            setLoading(true);
            setError("");

            const params = {
                page: selectedPage,
                limit: 20
            };

            if (searchText.trim()) {

                params.name =
                    searchText.trim();
            }

            const response =
                await api.get(
                    "/products",
                    {
                        params
                    }
                );

            setProducts(
                response.data.products || []
            );

            setPage(
                response.data.page || 1
            );

            setTotalPages(
                response.data.total_pages || 1
            );

            setTotalProducts(
                response.data.total_products || 0
            );

        } catch (error) {

            setError(
                error.response?.data?.message ||
                "Unable to load products"
            );

        } finally {

            setLoading(false);
        }
    };


    useEffect(() => {

        loadAccess();

    }, []);


    useEffect(() => {

        const timer =
            setTimeout(() => {

                loadProducts(
                    1,
                    search
                );

            }, 300);

        return () =>
            clearTimeout(
                timer
            );

    }, [search]);


    // ========================================
    // FORMATTERS
    // ========================================

    const formatNumber = (
        value,
        maximumFractionDigits = 2
    ) => {

        if (
            value === null ||
            value === undefined ||
            value === ""
        ) {

            return "—";
        }

        const numericValue =
            Number(value);

        if (
            !Number.isFinite(
                numericValue
            )
        ) {

            return String(value);
        }

        return numericValue.toLocaleString(
            "en-US",
            {
                minimumFractionDigits: 0,
                maximumFractionDigits
            }
        );
    };


    const formatPrice = (
        value
    ) => {

        if (
            value === null ||
            value === undefined ||
            value === ""
        ) {

            return "—";
        }

        return `TSh ${formatNumber(
            value,
            2
        )}`;
    };


    const displayText = (
        value
    ) => {

        if (
            value === null ||
            value === undefined ||
            String(value).trim() === ""
        ) {

            return "—";
        }

        return String(value);
    };


    const getImageURL = (
        imagePath
    ) => {

        if (!imagePath) {
            return null;
        }

        if (
            String(imagePath).startsWith(
                "http://"
            ) ||
            String(imagePath).startsWith(
                "https://"
            )
        ) {

            return imagePath;
        }

        const cleanPath =
            String(imagePath).startsWith("/")
                ? String(imagePath)
                : `/${imagePath}`;

        return `${serverRoot}${cleanPath}`;
    };


    // ========================================
    // EDIT CELL
    // ========================================

    const startEdit = (
        product,
        field
    ) => {

        if (
            field !== "retail_price" &&
            userRole !== "OWNER"
        ) {

            return;
        }

        if (
            field === "retail_price" &&
            !hasPermission(
                "EDIT_PRICES"
            )
        ) {

            return;
        }

        setEditingCell({
            productId:
                product.id,
            field
        });

        const currentValue =
            product[field];

        setEditValue(
            currentValue === null ||
            currentValue === undefined
                ? ""
                : String(
                    currentValue
                )
        );
    };


    const cancelEdit = () => {

        if (savingCell) {
            return;
        }

        setEditingCell(null);
        setEditValue("");
    };


    // ========================================
    // SAVE CELL
    // ========================================

    const saveCell = async (
        product
    ) => {

        if (
            !editingCell ||
            savingCell
        ) {

            return;
        }

        const field =
            editingCell.field;

        try {

            setSavingCell(true);


            // PRODUCT NAME

            if (
                field ===
                "product_name"
            ) {

                const cleanName =
                    editValue
                        .trim()
                        .replace(
                            /\s+/g,
                            " "
                        );

                if (!cleanName) {

                    alert(
                        "Product name cannot be empty."
                    );

                    return;
                }

                await api.patch(
                    `/products/${product.id}/name`,
                    {
                        product_name:
                            cleanName
                    }
                );
            }


            // SELLING PRICE

            else if (
                field ===
                "retail_price"
            ) {

                if (
                    editValue === "" ||
                    !Number.isFinite(
                        Number(
                            editValue
                        )
                    ) ||
                    Number(
                        editValue
                    ) < 0
                ) {

                    alert(
                        "Please enter a valid selling price."
                    );

                    return;
                }

                await api.patch(
                    `/products/${product.id}/price`,
                    {
                        retail_price:
                            Number(
                                editValue
                            )
                    }
                );
            }


            // PRODUCT DETAILS

            else {

                let value =
                    editValue;

                if (
                    field === "ctn" ||
                    field === "quantity" ||
                    field === "cost_price"
                ) {

                    if (
                        editValue === ""
                    ) {

                        value = null;

                    } else {

                        const numericValue =
                            Number(
                                editValue
                            );

                        if (
                            !Number.isFinite(
                                numericValue
                            ) ||
                            numericValue < 0
                        ) {

                            alert(
                                "Please enter a valid non-negative number."
                            );

                            return;
                        }

                        value =
                            numericValue;
                    }

                } else {

                    value =
                        editValue.trim() === ""
                            ? null
                            : editValue.trim();
                }

                await api.patch(
                    `/products/${product.id}/details`,
                    {
                        [field]:
                            value
                    }
                );
            }


            setEditingCell(null);
            setEditValue("");

            await loadProducts(
                page,
                search
            );

        } catch (error) {

            alert(
                error.response?.data?.message ||
                "Unable to save product"
            );

        } finally {

            setSavingCell(false);
        }
    };


    // ========================================
    // STOCK STATUS
    // ========================================

    const changeStockStatus =
        async (
            product,
            newStatus
        ) => {

            if (
                !canManageStock ||
                changingStockId
            ) {

                return;
            }

            try {

                setChangingStockId(
                    product.id
                );

                await api.patch(
                    `/products/${product.id}/stock-status`,
                    {
                        stock_status:
                            newStatus
                    }
                );

                setProducts(
                    currentProducts =>
                        currentProducts.map(
                            currentProduct => {

                                if (
                                    currentProduct.id !==
                                    product.id
                                ) {

                                    return currentProduct;
                                }

                                return {
                                    ...currentProduct,
                                    stock_status:
                                        newStatus
                                };
                            }
                        )
                );

            } catch (error) {

                alert(
                    error.response?.data?.message ||
                    "Unable to change stock status"
                );

            } finally {

                setChangingStockId(
                    null
                );
            }
        };


    // ========================================
    // PRODUCT IMAGE
    // ========================================

    const chooseProductImage = (
        product
    ) => {

        if (
            userRole !== "OWNER"
        ) {

            if (
                product.image_path
            ) {

                setImagePreview(
                    getImageURL(
                        product.image_path
                    )
                );
            }

            return;
        }

        selectedImageProductRef.current =
            product;

        if (
            imageInputRef.current
        ) {

            imageInputRef.current.value =
                "";

            imageInputRef.current.click();
        }
    };


    const uploadProductImage =
        async (
            event
        ) => {

            const file =
                event.target.files?.[0];

            const product =
                selectedImageProductRef.current;

            if (
                !file ||
                !product
            ) {

                return;
            }

            const allowedTypes = [
                "image/jpeg",
                "image/png",
                "image/webp"
            ];

            if (
                !allowedTypes.includes(
                    file.type
                )
            ) {

                alert(
                    "Please choose a JPG, PNG or WEBP image."
                );

                event.target.value =
                    "";

                return;
            }

            if (
                file.size >
                5 * 1024 * 1024
            ) {

                alert(
                    "Picture must be 5 MB or smaller."
                );

                event.target.value =
                    "";

                return;
            }

            try {

                setUploadingProductId(
                    product.id
                );

                const formData =
                    new FormData();

                formData.append(
                    "image",
                    file
                );

                await api.patch(
                    `/products/${product.id}/image`,
                    formData
                );

                await loadProducts(
                    page,
                    search
                );

            } catch (error) {

                alert(
                    error.response?.data?.message ||
                    "Unable to upload product picture"
                );

            } finally {

                setUploadingProductId(
                    null
                );

                selectedImageProductRef.current =
                    null;

                event.target.value =
                    "";
            }
        };

            // ========================================
    // DELETE PRODUCT POPUP
    // ========================================

    const requestDeleteProduct = (
        product
    ) => {

        if (
            userRole !== "OWNER" ||
            deletingProductId
        ) {

            return;
        }

        setPendingDeleteProduct(
            product
        );
    };


    const closeDeletePopup = () => {

        if (deletingProductId) {
            return;
        }

        setPendingDeleteProduct(
            null
        );
    };


    const confirmDeleteProduct =
        async () => {

            if (
                !pendingDeleteProduct ||
                userRole !== "OWNER" ||
                deletingProductId
            ) {

                return;
            }

            const product =
                pendingDeleteProduct;

            try {

                setDeletingProductId(
                    product.id
                );

                await api.delete(
                    `/products/${product.id}`
                );

                setPendingDeleteProduct(
                    null
                );

                const nextPage =
                    products.length === 1 &&
                    page > 1
                        ? page - 1
                        : page;

                await loadProducts(
                    nextPage,
                    search
                );

            } catch (error) {

                alert(
                    error.response?.data?.message ||
                    "Unable to delete product"
                );

            } finally {

                setDeletingProductId(
                    null
                );
            }
        };


    // ========================================
    // CREATE PRODUCT
    // ========================================

    const openCreateProduct = () => {

        setNewProductName("");
        setNewProductPrice("");

        setShowCreateProduct(
            true
        );
    };


    const closeCreateProduct = () => {

        if (creatingProduct) {
            return;
        }

        setShowCreateProduct(
            false
        );

        setNewProductName("");
        setNewProductPrice("");
    };


    const createProduct =
        async () => {

            const cleanName =
                newProductName
                    .trim()
                    .replace(
                        /\s+/g,
                        " "
                    );

            const numericPrice =
                Number(
                    newProductPrice
                );


            if (!cleanName) {

                alert(
                    "Please enter a product name."
                );

                return;
            }


            if (
                newProductPrice === "" ||
                !Number.isFinite(
                    numericPrice
                ) ||
                numericPrice < 0
            ) {

                alert(
                    "Please enter a valid selling price."
                );

                return;
            }


            try {

                setCreatingProduct(
                    true
                );

                await api.post(
                    "/products",
                    {
                        product_name:
                            cleanName,

                        retail_price:
                            numericPrice
                    }
                );

                setShowCreateProduct(
                    false
                );

                setNewProductName("");
                setNewProductPrice("");
                setSearch("");

                await loadProducts(
                    1,
                    ""
                );

            } catch (error) {

                alert(
                    error.response?.data?.message ||
                    "Unable to create product"
                );

            } finally {

                setCreatingProduct(
                    false
                );
            }
        };


    // ========================================
    // EDITABLE CELL
    // ========================================

    const EditableCell = ({
        product,
        field,
        type = "text",
        children,
        canEdit = false
    }) => {

        const isEditing =
            editingCell?.productId ===
                product.id &&
            editingCell?.field ===
                field;


        if (isEditing) {

            return (

                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px"
                    }}
                >

                    <input
                        type={type}
                        min={
                            type === "number"
                                ? "0"
                                : undefined
                        }
                        value={editValue}
                        autoFocus
                        onChange={(e) =>
                            setEditValue(
                                e.target.value
                            )
                        }
                        onKeyDown={(e) => {

                            if (
                                e.key === "Enter"
                            ) {

                                saveCell(
                                    product
                                );
                            }

                            if (
                                e.key === "Escape"
                            ) {

                                cancelEdit();
                            }
                        }}
                        style={{
                            width:
                                field === "location"
                                    ? "130px"
                                    : field === "product_name"
                                    ? "180px"
                                    : field === "item_code"
                                    ? "120px"
                                    : "90px",

                            padding: "7px 8px",

                            border:
                                "1px solid #b9b9b9",

                            borderRadius:
                                "7px",

                            fontSize:
                                "13px"
                        }}
                    />


                    <button
                        type="button"
                        onClick={() =>
                            saveCell(
                                product
                            )
                        }
                        disabled={
                            savingCell
                        }
                        title="Save"
                        style={{
                            border: "none",
                            background:
                                "transparent",
                            cursor:
                                "pointer",
                            padding:
                                "3px"
                        }}
                    >
                        <Check
                            size={17}
                        />
                    </button>


                    <button
                        type="button"
                        onClick={
                            cancelEdit
                        }
                        disabled={
                            savingCell
                        }
                        title="Cancel"
                        style={{
                            border: "none",
                            background:
                                "transparent",
                            cursor:
                                "pointer",
                            padding:
                                "3px"
                        }}
                    >
                        <X
                            size={17}
                        />
                    </button>

                </div>
            );
        }


        return (

            <button
                type="button"
                onClick={() => {

                    if (canEdit) {

                        startEdit(
                            product,
                            field
                        );
                    }
                }}
                style={{
                    border: "none",
                    background:
                        "transparent",
                    padding: 0,
                    margin: 0,
                    font: "inherit",
                    color: "inherit",

                    cursor:
                        canEdit
                            ? "pointer"
                            : "default",

                    display:
                        "flex",

                    alignItems:
                        "center",

                    gap:
                        "5px",

                    textAlign:
                        "left",

                    whiteSpace:
                        "nowrap"
                }}
            >

                {children}

                {canEdit && (

                    <Pencil
                        size={12}
                        style={{
                            opacity:
                                0.45
                        }}
                    />

                )}

            </button>
        );
    };


    // ========================================
    // RENDER
    // ========================================

    return (

        <div className="mobile-app">


            {/* HIDDEN IMAGE INPUT */}

            <input
                ref={imageInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={
                    uploadProductImage
                }
                style={{
                    display:
                        "none"
                }}
            />


            {/* HEADER */}

            <header className="page-header">

                <button
                    className="back-button"
                    onClick={() =>
                        navigate(
                            "/dashboard"
                        )
                    }
                >
                    <ArrowLeft
                        size={21}
                    />
                </button>


                <div>

                    <h1>
                        Products
                    </h1>

                    <p>
                        {totalProducts}{" "}

                        {totalProducts === 1
                            ? "product"
                            : "products"
                        }
                    </p>

                </div>

            </header>


            <main className="products-content">


                {/* SEARCH */}

                <div className="product-search">

                    <Search
                        size={20}
                        className="search-icon"
                    />

                    <input
                        type="text"
                        placeholder="Search products..."
                        value={search}
                        onChange={(e) =>
                            setSearch(
                                e.target.value
                            )
                        }
                        autoComplete="off"
                    />

                    {search && (

                        <button
                            type="button"
                            className="clear-search"
                            onClick={() =>
                                setSearch("")
                            }
                        >
                            ×
                        </button>

                    )}

                </div>


                {/* ADD PRODUCT */}

                {hasPermission(
                    "CREATE_PRODUCTS"
                ) && (

                    <button
                        type="button"
                        className="create-product-button"
                        onClick={
                            openCreateProduct
                        }
                    >
                        <Plus
                            size={18}
                        />

                        Add Product
                    </button>

                )}


                {error && (

                    <div className="error-message">
                        {error}
                    </div>

                )}


                {/* TABLE */}

                {loading ? (

                    <div className="loading-state">
                        Loading products...
                    </div>

                ) : products.length === 0 ? (

                    <div className="empty-state">
                        No products found.
                    </div>

                ) : (

                    <div
                        style={{
                            width: "100%",
                            overflowX: "auto",

                            WebkitOverflowScrolling:
                                "touch",

                            marginTop:
                                "14px",

                            borderRadius:
                                "12px",

                            border:
                                "1px solid #e7e7e7",

                            background:
                                "#fff",

                            position:
                                "relative"
                        }}
                    >

                        <table
                            style={{
                                width:
                                    "100%",

                                minWidth:
                                    tableMinWidth,

                                borderCollapse:
                                    "separate",

                                borderSpacing:
                                    0,

                                fontSize:
                                    "13px"
                            }}
                        >

                            <thead>

                                <tr
                                    style={{
                                        background:
                                            "#f7f7f7"
                                    }}
                                >

                                    <th
                                        style={{
                                            ...headerStyle,
                                            ...stickyHeaderStyle
                                        }}
                                    >
                                        Product Name
                                    </th>


                                    {canViewSellingPrice && (

                                        <th
                                            style={
                                                headerStyle
                                            }
                                        >
                                            SP
                                        </th>

                                    )}


                                    {canViewUnit && (

                                        <th
                                            style={
                                                headerStyle
                                            }
                                        >
                                            Unit
                                        </th>

                                    )}


                                    {canViewCTN && (

                                        <th
                                            style={
                                                headerStyle
                                            }
                                        >
                                            CTN
                                        </th>

                                    )}


                                    {canViewLocation && (

                                        <th
                                            style={
                                                headerStyle
                                            }
                                        >
                                            Location
                                        </th>

                                    )}


                                    {canViewQuantity && (

                                        <th
                                            style={
                                                headerStyle
                                            }
                                        >
                                            Quantity
                                        </th>

                                    )}


                                    {canViewCostPrice && (

                                        <th
                                            style={
                                                headerStyle
                                            }
                                        >
                                            CP
                                        </th>

                                    )}


                                    {canViewItemCode && (

                                        <th
                                            style={
                                                headerStyle
                                            }
                                        >
                                            Item Code
                                        </th>

                                    )}


                                    <th
                                        style={
                                            headerStyle
                                        }
                                    >
                                        Stock
                                    </th>


                                    {userRole ===
                                        "OWNER" && (

                                        <th
                                            style={{
                                                ...headerStyle,
                                                textAlign:
                                                    "center"
                                            }}
                                        >
                                            Delete
                                        </th>

                                    )}

                                </tr>

                            </thead>


                            <tbody>

                                {products.map(
                                    product => {

                                        const actualStockStatus =
                                            product.stock_status ||
                                            "IN_STOCK";

                                        const isInStock =
                                            actualStockStatus ===
                                            "IN_STOCK";

                                        const productNameColor =
                                            isInStock
                                                ? "#15803d"
                                                : "#dc2626";

                                        const imageURL =
                                            getImageURL(
                                                product.image_path
                                            );

                                        const isUploading =
                                            uploadingProductId ===
                                            product.id;

                                        const isChangingStock =
                                            changingStockId ===
                                            product.id;

                                        const isDeleting =
                                            deletingProductId ===
                                            product.id;


                                        return (

                                            <tr
                                                key={
                                                    product.id
                                                }
                                            >

                                                {/* PRODUCT NAME */}

                                                <td
                                                    style={{
                                                        ...cellStyle,
                                                        ...stickyCellStyle
                                                    }}
                                                >

                                                    <div
                                                        style={{
                                                            display:
                                                                "flex",

                                                            alignItems:
                                                                "center",

                                                            gap:
                                                                "8px",

                                                            minWidth:
                                                                0
                                                        }}
                                                    >

                                                        {/* PICTURE */}

                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                chooseProductImage(
                                                                    product
                                                                )
                                                            }
                                                            disabled={
                                                                isUploading
                                                            }
                                                            title={
                                                                userRole ===
                                                                "OWNER"
                                                                    ? product.image_path
                                                                        ? "Change picture"
                                                                        : "Add picture"
                                                                    : product.image_path
                                                                    ? "View picture"
                                                                    : "No picture"
                                                            }
                                                            style={{
                                                                width:
                                                                    "34px",

                                                                height:
                                                                    "34px",

                                                                minWidth:
                                                                    "34px",

                                                                border:
                                                                    "1px solid #e1e1e1",

                                                                borderRadius:
                                                                    "7px",

                                                                padding:
                                                                    0,

                                                                overflow:
                                                                    "hidden",

                                                                display:
                                                                    "flex",

                                                                alignItems:
                                                                    "center",

                                                                justifyContent:
                                                                    "center",

                                                                background:
                                                                    "#f7f7f7",

                                                                cursor:
                                                                    userRole === "OWNER" ||
                                                                    product.image_path
                                                                        ? "pointer"
                                                                        : "default"
                                                            }}
                                                        >

                                                            {isUploading ? (

                                                                <span
                                                                    style={{
                                                                        fontSize:
                                                                            "10px"
                                                                    }}
                                                                >
                                                                    ...
                                                                </span>

                                                            ) : imageURL ? (

                                                                <img
                                                                    src={
                                                                        imageURL
                                                                    }
                                                                    alt=""
                                                                    style={{
                                                                        width:
                                                                            "100%",

                                                                        height:
                                                                            "100%",

                                                                        objectFit:
                                                                            "cover"
                                                                    }}
                                                                />

                                                            ) : userRole ===
                                                              "OWNER" ? (

                                                                <Camera
                                                                    size={16}
                                                                />

                                                            ) : (

                                                                <ImageIcon
                                                                    size={15}
                                                                    style={{
                                                                        opacity:
                                                                            0.45
                                                                    }}
                                                                />

                                                            )}

                                                        </button>


                                                        {/* NAME */}

                                                        <div
                                                            style={{
                                                                minWidth:
                                                                    0
                                                            }}
                                                        >

                                                            <EditableCell
                                                                product={
                                                                    product
                                                                }
                                                                field="product_name"
                                                                canEdit={
                                                                    userRole ===
                                                                    "OWNER"
                                                                }
                                                            >

                                                                <strong
                                                                    style={{
                                                                        color:
                                                                            productNameColor
                                                                    }}
                                                                >
                                                                    {
                                                                        product.product_name
                                                                    }
                                                                </strong>

                                                            </EditableCell>

                                                        </div>

                                                    </div>

                                                </td>


                                                {/* SP */}

                                                {canViewSellingPrice && (

                                                    <td
                                                        style={
                                                            cellStyle
                                                        }
                                                    >

                                                        <EditableCell
                                                            product={
                                                                product
                                                            }
                                                            field="retail_price"
                                                            type="number"
                                                            canEdit={
                                                                hasPermission(
                                                                    "EDIT_PRICES"
                                                                )
                                                            }
                                                        >

                                                            <strong>
                                                                {
                                                                    formatPrice(
                                                                        product.retail_price
                                                                    )
                                                                }
                                                            </strong>

                                                        </EditableCell>

                                                    </td>

                                                )}

                                                                                                {/* UNIT */}

                                                {canViewUnit && (

                                                    <td
                                                        style={
                                                            cellStyle
                                                        }
                                                    >

                                                        <EditableCell
                                                            product={
                                                                product
                                                            }
                                                            field="unit"
                                                            canEdit={
                                                                userRole ===
                                                                "OWNER"
                                                            }
                                                        >
                                                            {
                                                                displayText(
                                                                    product.unit
                                                                )
                                                            }
                                                        </EditableCell>

                                                    </td>

                                                )}


                                                {/* CTN */}

                                                {canViewCTN && (

                                                    <td
                                                        style={
                                                            cellStyle
                                                        }
                                                    >

                                                        <EditableCell
                                                            product={
                                                                product
                                                            }
                                                            field="ctn"
                                                            type="number"
                                                            canEdit={
                                                                userRole ===
                                                                "OWNER"
                                                            }
                                                        >
                                                            {
                                                                formatNumber(
                                                                    product.ctn
                                                                )
                                                            }
                                                        </EditableCell>

                                                    </td>

                                                )}


                                                {/* LOCATION */}

                                                {canViewLocation && (

                                                    <td
                                                        style={
                                                            cellStyle
                                                        }
                                                    >

                                                        <EditableCell
                                                            product={
                                                                product
                                                            }
                                                            field="location"
                                                            canEdit={
                                                                userRole ===
                                                                "OWNER"
                                                            }
                                                        >
                                                            {
                                                                displayText(
                                                                    product.location
                                                                )
                                                            }
                                                        </EditableCell>

                                                    </td>

                                                )}


                                                {/* QUANTITY */}

                                                {canViewQuantity && (

                                                    <td
                                                        style={
                                                            cellStyle
                                                        }
                                                    >

                                                        <EditableCell
                                                            product={
                                                                product
                                                            }
                                                            field="quantity"
                                                            type="number"
                                                            canEdit={
                                                                userRole ===
                                                                "OWNER"
                                                            }
                                                        >
                                                            {
                                                                formatNumber(
                                                                    product.quantity
                                                                )
                                                            }
                                                        </EditableCell>

                                                    </td>

                                                )}


                                                {/* COST PRICE */}

                                                {canViewCostPrice && (

                                                    <td
                                                        style={
                                                            cellStyle
                                                        }
                                                    >

                                                        <EditableCell
                                                            product={
                                                                product
                                                            }
                                                            field="cost_price"
                                                            type="number"
                                                            canEdit={
                                                                userRole ===
                                                                "OWNER"
                                                            }
                                                        >
                                                            <strong>
                                                                {
                                                                    formatPrice(
                                                                        product.cost_price
                                                                    )
                                                                }
                                                            </strong>

                                                        </EditableCell>

                                                    </td>

                                                )}


                                                {/* ITEM CODE */}

                                                {canViewItemCode && (

                                                    <td
                                                        style={
                                                            cellStyle
                                                        }
                                                    >

                                                        <EditableCell
                                                            product={
                                                                product
                                                            }
                                                            field="item_code"
                                                            canEdit={
                                                                userRole ===
                                                                "OWNER"
                                                            }
                                                        >
                                                            {
                                                                displayText(
                                                                    product.item_code
                                                                )
                                                            }
                                                        </EditableCell>

                                                    </td>

                                                )}


                                                {/* STOCK */}

                                                <td
                                                    style={
                                                        cellStyle
                                                    }
                                                >

                                                    <button
                                                        type="button"
                                                        disabled={
                                                            !canManageStock ||
                                                            isChangingStock
                                                        }
                                                        onClick={() =>
                                                            changeStockStatus(
                                                                product,
                                                                isInStock
                                                                    ? "OUT_OF_STOCK"
                                                                    : "IN_STOCK"
                                                            )
                                                        }
                                                        title={
                                                            canManageStock
                                                                ? "Click to change stock status"
                                                                : undefined
                                                        }
                                                        style={{
                                                            border:
                                                                "none",

                                                            borderRadius:
                                                                "999px",

                                                            padding:
                                                                "6px 10px",

                                                            fontSize:
                                                                "11px",

                                                            fontWeight:
                                                                700,

                                                            whiteSpace:
                                                                "nowrap",

                                                            background:
                                                                isInStock
                                                                    ? "#dcfce7"
                                                                    : "#fee2e2",

                                                            color:
                                                                isInStock
                                                                    ? "#166534"
                                                                    : "#991b1b",

                                                            cursor:
                                                                canManageStock
                                                                    ? "pointer"
                                                                    : "default",

                                                            opacity:
                                                                isChangingStock
                                                                    ? 0.55
                                                                    : 1
                                                        }}
                                                    >

                                                        {isChangingStock
                                                            ? "Saving..."
                                                            : isInStock
                                                            ? "IN STOCK"
                                                            : "OUT OF STOCK"
                                                        }

                                                    </button>

                                                </td>


                                                {/* DELETE */}

                                                {userRole ===
                                                    "OWNER" && (

                                                    <td
                                                        style={{
                                                            ...cellStyle,
                                                            textAlign:
                                                                "center"
                                                        }}
                                                    >

                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                requestDeleteProduct(
                                                                    product
                                                                )
                                                            }
                                                            disabled={
                                                                isDeleting
                                                            }
                                                            title="Delete product"
                                                            style={{
                                                                border:
                                                                    "none",

                                                                background:
                                                                    "transparent",

                                                                color:
                                                                    "#dc2626",

                                                                cursor:
                                                                    isDeleting
                                                                        ? "default"
                                                                        : "pointer",

                                                                padding:
                                                                    "6px",

                                                                display:
                                                                    "inline-flex",

                                                                alignItems:
                                                                    "center",

                                                                justifyContent:
                                                                    "center",

                                                                opacity:
                                                                    isDeleting
                                                                        ? 0.45
                                                                        : 1
                                                            }}
                                                        >

                                                            <Trash2
                                                                size={18}
                                                            />

                                                        </button>

                                                    </td>

                                                )}

                                            </tr>
                                        );
                                    }
                                )}

                            </tbody>

                        </table>

                    </div>

                )}


                {/* PAGINATION */}

                {!loading &&
                    products.length > 0 &&
                    totalPages > 1 && (

                    <div className="pagination">

                        <button
                            disabled={
                                page <= 1
                            }
                            onClick={() =>
                                loadProducts(
                                    page - 1,
                                    search
                                )
                            }
                        >
                            <ChevronLeft
                                size={19}
                            />
                        </button>


                        <span>
                            Page {page} of{" "}
                            {totalPages}
                        </span>


                        <button
                            disabled={
                                page >=
                                totalPages
                            }
                            onClick={() =>
                                loadProducts(
                                    page + 1,
                                    search
                                )
                            }
                        >
                            <ChevronRight
                                size={19}
                            />
                        </button>

                    </div>

                )}

            </main>


            {/* ========================================
                CREATE PRODUCT POPUP
            ======================================== */}

            {showCreateProduct && (

                <div
                    className="product-modal-overlay"
                    onClick={
                        closeCreateProduct
                    }
                >

                    <div
                        className="product-modal create-product-modal"
                        onClick={(e) =>
                            e.stopPropagation()
                        }
                    >

                        <div
                            className="modal-handle"
                        />


                        <div className="product-modal-header">

                            <div>

                                <p>
                                    New Product
                                </p>

                                <h2>
                                    Add Product
                                </h2>

                            </div>


                            <button
                                type="button"
                                className="modal-close"
                                onClick={
                                    closeCreateProduct
                                }
                            >
                                <X
                                    size={20}
                                />
                            </button>

                        </div>


                        <div className="form-group">

                            <label>
                                Product Name
                            </label>

                            <input
                                type="text"
                                placeholder="Enter product name"
                                value={
                                    newProductName
                                }
                                onChange={(e) =>
                                    setNewProductName(
                                        e.target.value
                                    )
                                }
                                autoComplete="off"
                            />

                        </div>


                        <div className="form-group">

                            <label>
                                Selling Price (SP)
                            </label>

                            <div className="create-price-input">

                                <span>
                                    TSh
                                </span>

                                <input
                                    type="number"
                                    min="0"
                                    placeholder="15000"
                                    value={
                                        newProductPrice
                                    }
                                    onChange={(e) =>
                                        setNewProductPrice(
                                            e.target.value
                                        )
                                    }
                                />

                            </div>

                        </div>


                        <button
                            type="button"
                            className="primary-button"
                            onClick={
                                createProduct
                            }
                            disabled={
                                creatingProduct
                            }
                        >

                            {creatingProduct
                                ? "Creating..."
                                : "Create Product"
                            }

                        </button>

                    </div>

                </div>

            )}


            {/* ========================================
                DELETE PRODUCT POPUP
            ======================================== */}

            {pendingDeleteProduct && (

                <div
                    onClick={
                        closeDeletePopup
                    }
                    style={{
                        position:
                            "fixed",

                        inset:
                            0,

                        background:
                            "rgba(0, 0, 0, 0.45)",

                        zIndex:
                            10000,

                        display:
                            "flex",

                        alignItems:
                            "center",

                        justifyContent:
                            "center",

                        padding:
                            "20px"
                    }}
                >

                    <div
                        onClick={(e) =>
                            e.stopPropagation()
                        }
                        style={{
                            width:
                                "100%",

                            maxWidth:
                                "390px",

                            background:
                                "#ffffff",

                            borderRadius:
                                "18px",

                            padding:
                                "24px",

                            boxShadow:
                                "0 20px 60px rgba(0,0,0,0.22)"
                        }}
                    >

                        <div
                            style={{
                                width:
                                    "48px",

                                height:
                                    "48px",

                                borderRadius:
                                    "50%",

                                background:
                                    "#fee2e2",

                                display:
                                    "flex",

                                alignItems:
                                    "center",

                                justifyContent:
                                    "center",

                                marginBottom:
                                    "16px"
                            }}
                        >

                            <Trash2
                                size={23}
                                color="#dc2626"
                            />

                        </div>


                        <h2
                            style={{
                                margin:
                                    "0 0 8px",

                                fontSize:
                                    "20px",

                                color:
                                    "#222"
                            }}
                        >
                            Delete Product?
                        </h2>


                        <p
                            style={{
                                margin:
                                    "0 0 8px",

                                fontSize:
                                    "14px",

                                lineHeight:
                                    1.5,

                                color:
                                    "#555"
                            }}
                        >
                            Are you sure you want to delete{" "}

                            <strong>
                                {
                                    pendingDeleteProduct.product_name
                                }
                            </strong>
                            ?
                        </p>


                        <p
                            style={{
                                margin:
                                    "0 0 22px",

                                fontSize:
                                    "13px",

                                lineHeight:
                                    1.5,

                                color:
                                    "#888"
                            }}
                        >
                            This product will be permanently deleted.
                        </p>


                        <div
                            style={{
                                display:
                                    "flex",

                                gap:
                                    "10px"
                            }}
                        >

                            <button
                                type="button"
                                onClick={
                                    closeDeletePopup
                                }
                                disabled={
                                    Boolean(
                                        deletingProductId
                                    )
                                }
                                style={{
                                    flex:
                                        1,

                                    padding:
                                        "11px 14px",

                                    borderRadius:
                                        "10px",

                                    border:
                                        "1px solid #dddddd",

                                    background:
                                        "#ffffff",

                                    color:
                                        "#333333",

                                    fontSize:
                                        "14px",

                                    fontWeight:
                                        600,

                                    cursor:
                                        deletingProductId
                                            ? "default"
                                            : "pointer"
                                }}
                            >
                                Cancel
                            </button>


                            <button
                                type="button"
                                onClick={
                                    confirmDeleteProduct
                                }
                                disabled={
                                    Boolean(
                                        deletingProductId
                                    )
                                }
                                style={{
                                    flex:
                                        1,

                                    padding:
                                        "11px 14px",

                                    borderRadius:
                                        "10px",

                                    border:
                                        "none",

                                    background:
                                        "#dc2626",

                                    color:
                                        "#ffffff",

                                    fontSize:
                                        "14px",

                                    fontWeight:
                                        700,

                                    cursor:
                                        deletingProductId
                                            ? "default"
                                            : "pointer",

                                    opacity:
                                        deletingProductId
                                            ? 0.65
                                            : 1
                                }}
                            >

                                {deletingProductId
                                    ? "Deleting..."
                                    : "Delete Product"
                                }

                            </button>

                        </div>

                    </div>

                </div>

            )}

                        {/* ========================================
                IMAGE PREVIEW
            ======================================== */}

            {imagePreview && (

                <div
                    onClick={() =>
                        setImagePreview(
                            null
                        )
                    }
                    style={{
                        position:
                            "fixed",

                        inset:
                            0,

                        background:
                            "rgba(0,0,0,0.75)",

                        zIndex:
                            9999,

                        display:
                            "flex",

                        alignItems:
                            "center",

                        justifyContent:
                            "center",

                        padding:
                            "20px"
                    }}
                >

                    <div
                        onClick={(e) =>
                            e.stopPropagation()
                        }
                        style={{
                            position:
                                "relative",

                            maxWidth:
                                "520px",

                            width:
                                "100%"
                        }}
                    >

                        <button
                            type="button"
                            onClick={() =>
                                setImagePreview(
                                    null
                                )
                            }
                            style={{
                                position:
                                    "absolute",

                                right:
                                    "8px",

                                top:
                                    "8px",

                                zIndex:
                                    2,

                                border:
                                    "none",

                                width:
                                    "34px",

                                height:
                                    "34px",

                                borderRadius:
                                    "50%",

                                background:
                                    "#fff",

                                cursor:
                                    "pointer",

                                display:
                                    "flex",

                                alignItems:
                                    "center",

                                justifyContent:
                                    "center"
                            }}
                        >
                            <X
                                size={19}
                            />
                        </button>


                        <img
                            src={
                                imagePreview
                            }
                            alt="Product"
                            style={{
                                display:
                                    "block",

                                width:
                                    "100%",

                                maxHeight:
                                    "80vh",

                                objectFit:
                                    "contain",

                                borderRadius:
                                    "12px",

                                background:
                                    "#fff"
                            }}
                        />

                    </div>

                </div>

            )}

        </div>
    );
}


// ========================================
// TABLE STYLES
// ========================================

const headerStyle = {

    padding:
        "12px 14px",

    textAlign:
        "left",

    fontWeight:
        700,

    whiteSpace:
        "nowrap",

    color:
        "#444",

    borderBottom:
        "1px solid #e7e7e7"
};


const cellStyle = {

    padding:
        "12px 14px",

    textAlign:
        "left",

    verticalAlign:
        "middle",

    whiteSpace:
        "nowrap",

    borderBottom:
        "1px solid #eeeeee"
};


const stickyHeaderStyle = {

    position:
        "sticky",

    left:
        0,

    zIndex:
        5,

    background:
        "#f7f7f7",

    minWidth:
        "210px",

    maxWidth:
        "210px",

    boxShadow:
        "2px 0 4px rgba(0,0,0,0.06)"
};


const stickyCellStyle = {

    position:
        "sticky",

    left:
        0,

    zIndex:
        3,

    background:
        "#ffffff",

    minWidth:
        "210px",

    maxWidth:
        "210px",

    boxShadow:
        "2px 0 4px rgba(0,0,0,0.06)"
};


export default Products;